import React from "react";
import { Receipt, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-white py-16 px-4 flex flex-col">
      <div className="max-w-4xl mx-auto w-full">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-10">
          <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mb-6 text-exiser-primary">
            <Receipt className="w-12 h-12" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold uppercase text-gray-900 mb-4 tracking-tight">
            Refund &amp; Cancellation Policy
          </h1>
          <p className="text-gray-500 font-sans max-w-2xl">
            This Refund &amp; Cancellation Policy applies to all purchases made
            on the official website of{" "}
            <strong>EXISER PRIVATE LIMITED</strong> (
            <span className="font-mono">www.exiser.in</span>).
          </p>
        </div>

        {/* Content Card */}
        <div className="bg-gray-50 rounded-2xl shadow-xl border border-gray-100 p-6 md:p-10 text-left space-y-8">
          {/* Intro */}
          <section className="space-y-2">
            <p className="text-gray-700 font-sans">
              By making a payment to <strong>EXISER PRIVATE LIMITED</strong>{" "}
              (“Company”, “We”, “Us”), you (“User”, “You”) agree to the terms
              of this Refund &amp; Cancellation Policy.
            </p>
          </section>

          {/* 1. No Refund Policy */}
          <section>
            <h2 className="text-xl font-bold uppercase text-gray-900 mb-2">
              1. General No-Refund Policy
            </h2>
            <p className="text-gray-700 font-sans">
              All fees paid for the following are{" "}
              <strong>strictly non-refundable</strong>:
            </p>
            <ul className="list-disc pl-6 text-gray-700 font-sans space-y-1">
              <li>Online or offline courses</li>
              <li>Live or recorded workshops and webinars</li>
              <li>Coaching, mentoring, or consulting programs</li>
              <li>Memberships or subscriptions</li>
              <li>Digital products (videos, PDFs, recordings, toolkits, etc.)</li>
            </ul>
            <p className="text-gray-700 font-sans mt-2">
              Once a payment is successfully processed, it{" "}
              <strong>cannot be cancelled, reversed, or refunded</strong>,
              regardless of non-attendance, change of mind, or personal
              circumstances.
            </p>
          </section>

          {/* 2. Transfer / Rescheduling */}
          <section>
            <h2 className="text-xl font-bold uppercase text-gray-900 mb-2">
              2. Transfer / Rescheduling
            </h2>
            <p className="text-gray-700 font-sans">
              In limited situations, and purely at the discretion of the
              Company, we may allow:
            </p>
            <ul className="list-disc pl-6 text-gray-700 font-sans space-y-1">
              <li>Transfer to a future batch of the same program</li>
              <li>Transfer of a seat to another individual (with written approval)</li>
              <li>Adjustment of fees against another eligible program</li>
            </ul>
            <p className="text-gray-700 font-sans mt-2">Any such transfer:</p>
            <ul className="list-disc pl-6 text-gray-700 font-sans space-y-1">
              <li>Must be requested in writing within the communicated timeline</li>
              <li>Is subject to availability and Company approval</li>
              <li>
                Is a one-time courtesy adjustment and <strong>not a refund</strong>
              </li>
            </ul>
            <p className="text-gray-700 font-sans italic mt-2">
              The Company reserves the right to decline any transfer or
              rescheduling request without assigning any reason.
            </p>
          </section>

          {/* 3. Cancellation by Company */}
          <section>
            <h2 className="text-xl font-bold uppercase text-gray-900 mb-2">
              3. Cancellation or Rescheduling by Company
            </h2>
            <p className="text-gray-700 font-sans">
              If a program, workshop, or event is postponed, rescheduled, or
              cancelled by EXISER due to operational, technical, or unforeseen
              reasons, participants may be offered:
            </p>
            <ul className="list-disc pl-6 text-gray-700 font-sans space-y-1">
              <li>A seat in a future batch of the same program, or</li>
              <li>A credit note issued within 7 working days, sent to their registered email, usable for next eligible programme.</li>
            </ul>
            <p className="text-gray-700 font-sans mt-2">
              Under no circumstances shall this be treated as an obligation to
              issue a <strong>cash refund</strong>.
            </p>
          </section>

          {/* 4. Digital Products */}
          <section>
            <h2 className="text-xl font-bold uppercase text-gray-900 mb-2">
              4. Digital Products &amp; Online Content
            </h2>
            <p className="text-gray-700 font-sans">All digital products, including:</p>
            <ul className="list-disc pl-6 text-gray-700 font-sans space-y-1">
              <li>Online course access and learning portals</li>
              <li>Pre-recorded video or audio content</li>
              <li>Digital workbooks, PDFs, templates, and resources</li>
            </ul>
            <p className="text-gray-700 font-sans mt-2">
              are considered <strong>delivered</strong> once access is granted.
              Due to the nature of digital goods, <strong>no refunds</strong> shall be issued after access is provided.
            </p>
          </section>

          {/* 5. Chargebacks */}
          <section>
            <h2 className="text-xl font-bold uppercase text-gray-900 mb-2">
              5. Payment Disputes &amp; Chargebacks
            </h2>
            <p className="text-gray-700 font-sans">
              By making a payment, you agree not to raise unjustified chargebacks or disputes with your payment provider.
            </p>
            <ul className="list-disc pl-6 text-gray-700 font-sans space-y-1 mt-2">
              <li>Access to services may be revoked immediately</li>
              <li>Legal action may be pursued as per applicable law</li>
            </ul>
          </section>

          {/* 6. Results Disclaimer */}
          <section>
            <h2 className="text-xl font-bold uppercase text-gray-900 mb-2">
              6. No Guarantee of Results
            </h2>
            <p className="text-gray-700 font-sans">
              Fees are charged for access to training and learning experiences,
              <strong> not for guaranteed outcomes</strong>.
            </p>
          </section>

          {/* 7. Policy Changes */}
          <section>
            <h2 className="text-xl font-bold uppercase text-gray-900 mb-2">
              7. Changes to This Policy
            </h2>
            <p className="text-gray-700 font-sans">
              EXISER PRIVATE LIMITED reserves the right to modify this Policy
              at any time. Updated versions will be published on the website.
            </p>
          </section>

          {/* 8. Contact */}
          <section>
            <h2 className="text-xl font-bold uppercase text-gray-900 mb-2">
              8. Contact for Refund-Related Queries
            </h2>
            <p className="text-gray-700 font-sans">
              In case of special queries, please contact:
            </p>
            <div className="border-l-4 border-exiser-primary pl-4 mt-2">
              <p className="font-semibold">EXISER PRIVATE LIMITED</p>
              <p>
                Email:{" "}
                <a
                  href="mailto:support@exiser.in"
                  className="text-exiser-primary hover:text-exiser-accent font-medium"
                >
                  support@exiser.in
                </a>
              </p>
            </div>
          </section>
        </div>

        {/* Back Home */}
        <div className="mt-10 flex justify-center">
          <Link
            to="/"
            className="flex items-center gap-2 text-exiser-primary font-bold uppercase hover:text-exiser-accent transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Return Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;